/**
 * FeatureFlags Nuxt Plugin
 * Defines a Nuxt plugin for retrieving feature flags
 * Merges local flags overrides with LD flags
 * @returns { FeatureFlags } - feature flag list
 * @example
 * const { sampleFeatureFlag } = useFeatureFlags()
 * @category Plugin
 */

import type { FeatureFlags } from '#types/featureFlags'

declare module '#app' {
  interface NuxtApp {
    $feature: FeatureFlags
  }
}

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $feature: FeatureFlags
  }
}

export default defineNuxtPlugin({
  enforce: 'pre',
  async setup({ hook }) {
    const { launchDarklyClientKey } = useRuntimeConfig().public
    const experiments = useExperiments()
    const featureFlags = useFeatureFlags()
    const rawFeatureFlags = useRawFeatureFlags()
    const optimizelyLdRuntimeState = useOptimizelyLdRuntime()
    const edgeWorkerExperiments = useEdgeWorkerExperiments()
    const activeExperiments = useActiveExperiments()

    const route = useRoute()

    const queryParameter = ref(route.query.ldPassthrough?.toString() ? route.query.ldPassthrough?.toString() : '')
    const ldPassthrough = useFeatureFlagsPassthroughHeader().value || queryParameter.value
    const settings: any = useApiHeaders() || {}

    const headers = {
      ...settings,
      locale: useLocale(),
      ...(ldPassthrough ? { 'x-ld-passthrough': ldPassthrough } : {})
    }

    // The server plugin will not have run on CSR-only pages,
    // and we'll only have the default empty object state,
    // we need to fetch the flags from the Nitro endpoint via an ajax call
    if (!Object.keys(rawFeatureFlags).length)
      Object.assign(rawFeatureFlags, await $fetch('/fapi/featureFlags', { headers }))

    if (rawFeatureFlags.configP13n?.enableOptimizely) {
      hook('app:mounted', () => {
        const auth = useAuthStore()
        const { decide, decisions } = useOptimizely()

        optimizelyLdRuntimeState.value = {
          auth,
          decide,
          getDecision: (key) => decisions.value.get(key)
        }
      })
    }

    if (rawFeatureFlags.useLdClientSdk) {
      try {
        hook('app:mounted', async () => {
          // Imported at runtime so it gets split into its own chunk
          // and is only loaded when needed
          const { initialize } = await import('launchdarkly-js-client-sdk')

          const auth = useAuthStore()
          const user = useUserStore()
          const localFlags = useLocalFeatureFlags().value || rawFeatureFlags.localFlags

          // On CSR pages we can only get the passthrough request header from the server response of the initial AJAX call.
          // This depends on the header being appended to the AJAX call by the header modification extension being used.
          const clientHeaders = {
            ...headers,
            ...(rawFeatureFlags.ldPassthroughHeader && { 'x-ld-passthrough': rawFeatureFlags.ldPassthroughHeader })
          }

          const context = getLDContext(clientHeaders, activeExperiments.active)
          const client = initialize(launchDarklyClientKey, context)

          await client.waitForInitialization()

          const mergeFeatureFlags = async () => {
            const updatedFlags = { ...rawFeatureFlags, ...client.allFlags(), ...localFlags }
            if (JSON.stringify(rawFeatureFlags) !== JSON.stringify(updatedFlags))
              Object.assign(rawFeatureFlags, updatedFlags)

            // GLOBAL15-108759 (Tech debt): remove once flags are migrated over to Optimizely
            if (rawFeatureFlags.allowEdgeWorkerExperiments) {
              Object.entries(edgeWorkerExperiments.value).forEach(([key, value]) => {
                if (rawFeatureFlags[key]?.active) {
                  rawFeatureFlags[key].value = value
                }
              })
            }

            // If we haven't evaluated experiments yet or the experiments are active
            // The only reason is that if a client hits a CSR page directly, like cart or checkout, it won't be evaluated on SSR
            if (activeExperiments.active || !activeExperiments.evaluated) {
              Object.assign(experiments, getExperiments(featureFlags, context, client))
            }
          }

          // We need to merge feature flags immediately
          // because when we have experiments the value can be different from
          // default value provided from LD as experiments randomly provide
          // value from experiment variants
          // so we need to wait until client is initialized
          // because during the initialization it makes a call
          // to LD to fetch experiments
          await mergeFeatureFlags()

          // If we haven't evaluated experiments yet and there are experiments to run
          if (!activeExperiments.evaluated && Object.keys(experiments).length) {
            activeExperiments.active = true
            await client.identify(getLDContext(clientHeaders, true))
            await mergeFeatureFlags()
          }

          // Mark as evaluated (whether we had experiments or not)
          activeExperiments.evaluated = true

          // Reevaluate all flags on CSR.
          // Listening for changes in real time as intended
          // can have a negative effect on performance
          // due to the extensive usage of lazy hydration.
          // This is a compromise solution to get the client SDK in use
          // until a better solution can be found for making the real-time
          // flag evaluation work well with the app's use of lazy hydration
          useRouter().afterEach(mergeFeatureFlags)

          // re-identify the user context when logged-in status changes
          // after logging-in, wait for profile details to be fetched
          watch(() => auth.loggedIn, () => {
            if (auth.loggedIn) {
              whenever(() => user.details?.email, async () => {
                await client.identify(getLDContext(clientHeaders, activeExperiments.active, auth, user))
                await mergeFeatureFlags()
              }, { immediate: true })
            }
            else {
              whenever(() => useCookie('x-usid').value, async () => {
                await client.identify(getLDContext(clientHeaders, activeExperiments.active))
                await mergeFeatureFlags()
              }, { immediate: true })
            }
          }, { immediate: true })
        })
      }
      catch (err: any) {
        log.error('[Canvas:LD:Client] Failed to retrieve feature flags, using state', err)
      }
    }

    return {
      provide: {
        feature: featureFlags
      }
    }
  }
})
